import { ApiService } from './ApiService';

export interface ChannelLoginResponse {
  status: string;
  qrCode?: string;
}

export interface ChannelStatus {
  device_id: string;
  is_connected: boolean;
  is_logged_in: boolean;
  client_id?: string | null;
}

export interface ChannelStatusResponse {
  code: string;
  message: string;
  results: ChannelStatus;
}

export interface WhatsAppBotSettings {
  user_id: string;
  enabled: boolean;
  auto_reply_enabled: boolean;
  keywords: string[];
  keyword_mode: 'any' | 'all';
  whitelist_enabled: boolean;
  whitelisted_numbers: string[];
  blacklist_enabled: boolean;
  blacklisted_numbers: string[];
  welcome_message?: string;
  default_response?: string;
  keyword_responses: Record<string, string>;
  created_at?: string;
  updated_at?: string;
}

export interface WhatsAppMessage {
  message_id: string;
  from_number: string;
  from_name?: string;
  text?: string;
  timestamp: string;
  chat_id: string;
  is_group: boolean;
  replied: boolean;
  reply_text?: string;
}

export interface WhatsAppSettingsUpdate {
  enabled?: boolean;
  auto_reply_enabled?: boolean;
  keywords?: string[];
  keyword_mode?: 'any' | 'all';
  whitelist_enabled?: boolean;
  whitelisted_numbers?: string[];
  blacklist_enabled?: boolean;
  blacklisted_numbers?: string[];
  welcome_message?: string;
  default_response?: string;
  keyword_responses?: Record<string, string>;
}

export interface ChannelSettings {
  id: number;
  user_id: string;
  channel_type: string;
  enabled: boolean;
  settings: Record<string, any>;
  created_at: string;
  updated_at: string;
}

export interface ChannelSettingsUpdate {
  enabled?: boolean;
  settings?: Record<string, any>;
}

class ChannelService {
  private api: ApiService;

  constructor() {
    this.api = ApiService.getInstance();
  }

  /**
   * Initiate login for a channel and get QR code
   */
  async loginToChannel(channel: string = 'whatsapp'): Promise<{ blob?: Blob; alreadyConnected?: boolean; status?: string }> {
    try {
      const response = await this.api.get(`/api/channels/${channel}/login`, {
        responseType: 'blob'
      });
      
      // Check if response is JSON (already logged in case)
      const contentType = response.headers['content-type'];
      if (contentType && contentType.includes('application/json')) {
        // Convert blob to JSON
        const text = await response.data.text();
        const json = JSON.parse(text);
        return { alreadyConnected: true, status: json.status };
      }
      
      // Get status from header if available
      const status = response.headers['x-status'];
      return { blob: response.data, status };
    } catch (error) {
      console.error('Error logging into channel:', error);
      throw error;
    }
  }

  /**
   * Check channel connection status
   */
  async getChannelStatus(channel: string = 'whatsapp'): Promise<ChannelStatus> {
    try {
      const response = await this.api.get(`/api/channels/${channel}/status`);
      // Backend returns { code, message, results: { device_id, is_connected, is_logged_in } }
      if (response.data.results) {
        return response.data.results;
      }
      // Fallback for flat structure (if backend changes)
      return response.data;
    } catch (error) {
      console.error('Error checking channel status:', error);
      throw error;
    }
  }

  /**
   * Get WhatsApp bot settings
   */
  async getWhatsAppSettings(): Promise<WhatsAppBotSettings> {
    try {
      const response = await this.api.get('/api/channels/whatsapp/settings');
      return response.data;
    } catch (error) {
      console.error('Error getting WhatsApp settings:', error);
      throw error;
    }
  }

  /**
   * Update WhatsApp bot settings
   */
  async updateWhatsAppSettings(updates: WhatsAppSettingsUpdate): Promise<WhatsAppBotSettings> {
    try {
      const response = await this.api.put('/api/channels/whatsapp/settings', updates);
      return response.data;
    } catch (error) {
      console.error('Error updating WhatsApp settings:', error);
      throw error;
    }
  }

  /**
   * Get WhatsApp messages
   */
  async getWhatsAppMessages(limit: number = 50, offset: number = 0): Promise<WhatsAppMessage[]> {
    try {
      const response = await this.api.get('/api/channels/whatsapp/messages', {
        params: { limit, offset }
      });
      return response.data;
    } catch (error) {
      console.error('Error getting WhatsApp messages:', error);
      throw error;
    }
  }

  /**
   * Delete WhatsApp bot settings
   */
  async deleteWhatsAppSettings(): Promise<void> {
    try {
      await this.api.delete('/api/channels/whatsapp/settings');
    } catch (error) {
      console.error('Error deleting WhatsApp settings:', error);
      throw error;
    }
  }

  /**
   * Disconnect a channel
   */
  async disconnectChannel(channel: string = 'whatsapp'): Promise<void> {
    try {
      await this.api.post(`/api/channels/${channel}/disconnect`);
    } catch (error) {
      console.error('Error disconnecting channel:', error);
      throw error;
    }
  }

  /**
   * Get channel settings
   */
  async getChannelSettings(channel: string): Promise<ChannelSettings> {
    try {
      const response = await this.api.get(`/api/channels/${channel}/settings`);
      return response.data;
    } catch (error) {
      console.error('Error getting channel settings:', error);
      throw error;
    }
  }

  /**
   * Update channel settings
   */
  async updateChannelSettings(channel: string, updates: ChannelSettingsUpdate): Promise<ChannelSettings> {
    try {
      const response = await this.api.put(`/api/channels/${channel}/settings`, updates);
      return response.data;
    } catch (error) {
      console.error('Error updating channel settings:', error);
      throw error;
    }
  }
}

export const channelService = new ChannelService();
